import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AutoCompleteField from '../../../../core/components/Fields/AutoCompleteField.tsx';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import AddFieldDialog from './AddFieldDialog.tsx';
import type { AliasContextEntry } from '../types.ts';

interface FieldPathInputProps {
    value: string;
    onChange: (value: string) => void;
    aliasContext?: AliasContextEntry[];
    placeholder?: string;
    includeChainValue?: boolean;
}

const ADD_CUSTOM_FIELD_VALUE = '__ADD_CUSTOM_INPUT_FIELD__';

const FieldPathInput = ({
    value,
    onChange,
    aliasContext = [],
    placeholder = 'Select field path...',
    includeChainValue = true,
}: FieldPathInputProps) => {
    const { t } = useTranslation('integrations');
    const { getInputFields } = useOutputMapperEditor();
    const [showAddDialog, setShowAddDialog] = useState(false);

    const options = useMemo(() => {
        const fields = getInputFields(aliasContext);
        const filteredFields = includeChainValue ? fields : fields.filter((f) => f.path !== 'chain_value');
        const priority = (path: string) => (path === 'chain_value' ? 0 : path.startsWith('@') ? 1 : 2);
        const orderedFields = [...filteredFields].sort((a, b) => priority(a.path) - priority(b.path));
        return [
            ...orderedFields.map(({ path, description }) => ({
                value: path,
                label: path,
                description,
            })),
            { value: '__SEPARATOR__', label: '', role: 'separator' },
            {
                value: ADD_CUSTOM_FIELD_VALUE,
                label: t('outputMapper.visualEditor.addCustomInputField'),
                role: 'button',
            },
        ];
    }, [getInputFields, aliasContext, includeChainValue, t]);

    const filterOption = (optionValue: string, search: string, isDirty?: boolean) => {
        if (optionValue === '__SEPARATOR__' || optionValue === ADD_CUSTOM_FIELD_VALUE) return 1;
        if (!isDirty || !search) return 1;
        return optionValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
    };

    const handleSelect = (event: CustomEvent) => {
        if (event.detail.value === ADD_CUSTOM_FIELD_VALUE) {
            event.preventDefault();
            setShowAddDialog(true);
        }
    };

    return (
        <>
            <AutoCompleteField
                value={value}
                onValueChange={onChange}
                options={options}
                filterOption={filterOption}
                placeholder={placeholder}
                onSelect={handleSelect}
            />
            {showAddDialog && <AddFieldDialog mode="custom_input" onClose={() => setShowAddDialog(false)} />}
        </>
    );
};

export default FieldPathInput;
